"use client";

import useSWR from "swr";
import { createLogAdapter } from "@/lib/logger/console-adapter";

const uiLog = createLogAdapter("hooks/useNotifications.ts");

export interface NotificationMessage {
	id: string;
	type:
		| "ORDER_COMPLETED"
		| "ORDER_FAILED"
		| "ORDER_CANCELLED"
		| "PRICE_ALERT"
		| "SYSTEM";
	title: string;
	content: string;
	payload?: unknown;
	createdAt: string;
	read: boolean;
	clickable?: boolean;
	actionUrl?: string;
}

interface NotificationsResponse {
	notifications?: NotificationMessage[];
}

const fetchNotifications = async ([url, userPrincipal]: [
	string,
	string,
]): Promise<NotificationMessage[]> => {
	const response = await fetch(url, {
		headers: {
			"X-User-Principal": userPrincipal,
		},
	});

	if (!response.ok) {
		throw new Error(`Failed to load notifications: ${response.status}`);
	}

	const data = (await response.json()) as NotificationsResponse;
	return data.notifications || [];
};

export function useNotifications(userPrincipal?: string) {
	const swrKey = userPrincipal
		? (["/api/notifications/history", userPrincipal] as const)
		: null;

	const { data, error, isLoading, isValidating, mutate } = useSWR(
		swrKey,
		fetchNotifications,
		{
			refreshInterval: 30000,
			dedupingInterval: 5000,
		},
	);

	const notifications = data || [];
	const unreadCount = notifications.filter((notification) => !notification.read).length;

	const markAsRead = async (notificationId: string) => {
		if (!userPrincipal) return;

		await mutate(
			async (current = []) => {
				try {
					await fetch(`/api/notifications/${notificationId}/read`, {
						method: "POST",
						headers: {
							"X-User-Principal": userPrincipal,
						},
					});

					return current.map((notification) =>
						notification.id === notificationId
							? { ...notification, read: true }
							: notification,
					);
				} catch (error) {
					uiLog.error("标记通知已读失败:", error);
					throw error;
				}
			},
			{
				optimisticData: notifications.map((notification) =>
					notification.id === notificationId
						? { ...notification, read: true }
						: notification,
				),
				rollbackOnError: true,
				revalidate: false,
			},
		);
	};

	const markAllAsRead = async () => {
		if (!userPrincipal) return;

		await mutate(
			async (current = []) => {
				try {
					await fetch("/api/notifications/mark-all-read", {
						method: "POST",
						headers: {
							"X-User-Principal": userPrincipal,
						},
					});

					return current.map((notification) => ({
						...notification,
						read: true,
					}));
				} catch (error) {
					uiLog.error("标记全部通知已读失败:", error);
					throw error;
				}
			},
			{
				optimisticData: notifications.map((notification) => ({
					...notification,
					read: true,
				})),
				rollbackOnError: true,
				revalidate: false,
			},
		);
	};

	return {
		notifications,
		unreadCount,
		isLoading,
		isValidating,
		error,
		refresh: () => mutate(),
		markAsRead,
		markAllAsRead,
	};
}
